import React, { useMemo, useState } from 'react';
import { NameResult } from '../types/NameResult';
import { useFavoritesContext } from '../context/FavoritesContext';
import { NameResultCard } from './NameResultCard';
import { NameResultSkeletonGrid } from './NameResultSkeleton';
import {
  LuLayoutGrid as GridIcon,
  LuList as ListIcon,
} from 'react-icons/lu';
import { AnimatePresence, motion } from 'framer-motion';
import { EmptyState } from './EmptyState';

type Tab = 'all' | 'available' | 'saved';
type SortBy = 'score' | 'alpha' | 'available_first';
type ViewMode = 'grid' | 'list';

interface ResultsPaneProps {
  results: NameResult[];
  loading: boolean;
  onExampleClick: (keywords: string) => void;
  onDismiss: (name: string) => void;
  selectedTlds: string[];
}


export const ResultsPane: React.FC<ResultsPaneProps> = ({
  results,
  loading,
  onExampleClick,
  onDismiss,
  selectedTlds,
}) => {
  const { isFavorite } = useFavoritesContext();
  const [activeTab, setActiveTab] = useState<Tab>('all');
  const [sortBy, setSortBy] = useState<SortBy>('score');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const handleDismiss = (name: string) => {
    setDismissed((prev) => new Set(prev).add(name));
    onDismiss(name);
  };

  const visible = useMemo(() => {
    let filtered = results.filter((r) => !dismissed.has(r.name));
    if (activeTab === 'available') filtered = filtered.filter((r) => r.available === true);
    if (activeTab === 'saved') filtered = filtered.filter((r) => isFavorite(r.name));
    return [...filtered].sort((a, b) => {
      if (sortBy === 'score') return (b.score?.total_score ?? 0) - (a.score?.total_score ?? 0);
      if (sortBy === 'alpha') return a.name.localeCompare(b.name);
      if (sortBy === 'available_first') {
        if (a.available === b.available) return 0;
        return a.available ? -1 : 1;
      }
      return 0;
    });
  }, [results, dismissed, activeTab, sortBy, isFavorite]);

  const counts = useMemo(() => {
    const base = results.filter((r) => !dismissed.has(r.name));
    return {
      all: base.length,
      available: base.filter((r) => r.available === true).length,
      saved: base.filter((r) => isFavorite(r.name)).length,
    };
  }, [results, dismissed, isFavorite]);

  const showContent = !loading && results.length > 0;
  const showEmpty = !loading && results.length === 0;

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-[#09090f]">
      {/* Sticky header — always rendered so controls stay visible during load */}
      <div className="sticky top-0 z-10 bg-[#09090f] border-b border-[#131325] px-6 py-3 flex items-center gap-3">
        {/* Tabs */}
        <div className="flex items-center gap-1">
          {(['all', 'available', 'saved'] as Tab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab
                  ? 'bg-[#111120] text-white border border-[#2a2a4a]'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              <span className="capitalize">{tab}</span>
              <span
                className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold ${
                  activeTab === tab
                    ? 'bg-indigo-600/30 text-indigo-300'
                    : 'bg-[#131320] text-gray-500'
                }`}
              >
                {counts[tab]}
              </span>
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-3">
          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortBy)}
            className="text-sm bg-[#0f0f1a] border border-[#1a1a2e] text-gray-300 rounded-lg px-2.5 py-1.5 focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none pr-7"
            style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center', backgroundSize: '1.25em' }}
          >
            <option value="score">Best score</option>
            <option value="alpha">Name A–Z</option>
            <option value="available_first">Available first</option>
          </select>

          {/* View toggle */}
          <div className="flex bg-[#0f0f1a] border border-[#1a1a2e] rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 transition-colors ${
                viewMode === 'grid' ? 'bg-[#251c5c]/40 text-[#a89ee8]' : 'text-gray-500 hover:text-gray-300'
              }`}
              title="Grid view"
            >
              <GridIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 transition-colors ${
                viewMode === 'list' ? 'bg-[#251c5c]/40 text-[#a89ee8]' : 'text-gray-500 hover:text-gray-300'
              }`}
              title="List view"
            >
              <ListIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto flex flex-col">
        {loading && (
          <div className="px-6 py-8">
            <NameResultSkeletonGrid count={6} />
          </div>
        )}

        <AnimatePresence mode="wait">
          {showContent && (
            <motion.div
              key="results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="px-6 py-6"
            >
              {visible.length === 0 ? (
                <TabEmptyState tab={activeTab} />
              ) : (
                <div
                  className={
                    viewMode === 'grid'
                      ? 'grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(212px,1fr))]'
                      : 'flex flex-col gap-3'
                  }
                >
                  {visible.map((r, i) => (
                    <NameResultCard
                      key={r.name}
                      result={r}
                      index={i}
                      listView={viewMode === 'list'}
                      selectedTlds={selectedTlds}
                      onUpdate={() => {}}
                      onDismiss={() => handleDismiss(r.name)}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {showEmpty && <EmptyState key="empty" onExampleClick={onExampleClick} />}
        </AnimatePresence>
      </div>
    </div>
  );
};

function TabEmptyState({ tab }: { tab: Tab }) {
  if (tab === 'saved') {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-gray-600 text-sm">Star names you want to revisit — they'll appear here.</p>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <p className="text-gray-600 text-sm">No results match this filter.</p>
    </div>
  );
}
